import React, { useState } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useRouter } from '../context/RouterContext';
import Home from '../pages/Home';
import About from '../pages/About';
import PlateDetail from '../pages/PlateDetail';
import Privacy from '../pages/Privacy';
import Terms from '../pages/Terms';
import FAQ from '../pages/FAQ';
import Footer from './Footer';

interface ResponsiveLayoutProps {
  children?: React.ReactNode;
}

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({ children }) => {
  const { isMobile, isTablet } = useResponsive();
  const { currentRoute, navigate } = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const renderPage = () => {
    switch (currentRoute) {
      case 'about':
        return <About />;
      case 'privacy':
        return <Privacy />;
      case 'terms':
        return <Terms />;
      case 'faq':
        return <FAQ />;
      case 'plate-detail':
        return <PlateDetail />;
      case 'home':
      default:
        return <Home />;
    }
  };

  const heroTitleClass = "font-bold text-gray-900 dark:text-white";
  const heroSubtitleClass = "text-gray-600 dark:text-slate-400";
  const sidebarClass = "bg-white dark:bg-slate-800/50 shadow-soft-dark dark:shadow-soft-dark border-r border-gray-200 dark:border-slate-700/50";
  const sidebarContentClass = "text-gray-900 dark:text-white";
  const sidebarTextClass = "text-gray-600 dark:text-slate-400";
  const navButtonClass = "text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors w-full text-left";

  if (isMobile) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-slate-900 flex flex-col overflow-hidden transition-colors duration-300">
        {children}
        <main className="px-3 py-4 flex-1 min-h-0 overflow-y-auto">
          {currentRoute === 'home' && (
            <div className="mb-4">
              <h1 className={`text-2xl ${heroTitleClass} text-center mb-2`}>
                Rate My Plate
              </h1>
              <p className={`text-center ${heroSubtitleClass} text-sm`}>
                Discover funny license plate ideas, custom license plates, and personalized license plate inspiration from around the world
              </p>
            </div>
          )}
          {renderPage()}
        </main>
        <Footer />
      </div>
    );
  }

  if (isTablet) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-slate-900 flex flex-col overflow-hidden transition-colors duration-300">
        {children}
        <main className="container mx-auto px-4 py-6 flex-1 min-h-0 overflow-y-auto">
          {currentRoute === 'home' && (
            <div className="mb-6">
              <h1 className={`text-3xl ${heroTitleClass} text-center mb-3`}>
                Rate My Plate
              </h1>
              <p className={`text-center ${heroSubtitleClass} mb-6`}>
                Share and rate funny license plates, custom license plate ideas, and personalized plates from around the world
              </p>
            </div>
          )}
          {renderPage()}
        </main>
        <Footer />
      </div>
    );
  }

  // Desktop layout with sidebar
  return (
    <div className="h-screen bg-gray-50 dark:bg-slate-900 flex flex-col overflow-hidden transition-colors duration-300">
      {children}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {currentRoute === 'home' && (
          <aside
            className={`${sidebarClass} flex flex-col flex-shrink-0 h-full overflow-hidden transition-all duration-300 ${
              sidebarCollapsed ? 'w-14' : 'w-80'
            }`}
          >
            <div
              className={`flex p-3 border-b border-gray-100 dark:border-slate-700/50 ${
                sidebarCollapsed ? 'justify-center' : 'items-start justify-between'
              }`}
            >
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0 pr-2">
                  <h1 className={`text-xl font-bold ${sidebarContentClass} mb-1`}>
                    Rate My Plate
                  </h1>
                  <p className={`${sidebarTextClass} text-sm`}>
                    Discover funny license plate ideas, custom license plates, and personalized plate inspiration. Share and rate the coolest license plate ideas from our community.
                  </p>
                </div>
              )}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors flex-shrink-0"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg
                  className={`w-5 h-5 text-gray-600 dark:text-slate-400 transition-transform ${
                    sidebarCollapsed ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
            </div>

            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto p-3">
                  <div className="bg-gray-100 dark:bg-slate-700/30 rounded-xl p-3">
                    <h3 className={`font-semibold ${sidebarContentClass} mb-1`}>How it works</h3>
                    <ul className={`text-sm ${sidebarTextClass} space-y-1`}>
                      <li>• Upload custom license plates</li>
                      <li>• Vote on your favorites</li>
                      <li>• Browse by rating</li>
                      <li>• Share the fun!</li>
                    </ul>
                  </div>
                </div>

                <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-slate-700/50 space-y-3">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wider mb-1">Quick Links</h4>
                    <ul className="space-y-1">
                      <li><button onClick={() => navigate('home')} className={navButtonClass}>Home</button></li>
                      <li><button onClick={() => navigate('about')} className={navButtonClass}>About</button></li>
                      <li><button onClick={() => navigate('faq')} className={navButtonClass}>FAQ</button></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wider mb-1">Legal & Support</h4>
                    <ul className="space-y-1">
                      <li><button onClick={() => navigate('privacy')} className={navButtonClass}>Privacy Policy</button></li>
                      <li><button onClick={() => navigate('terms')} className={navButtonClass}>Terms of Service</button></li>
                      <li><a href="mailto:r8mypl8@gmail.com" className={navButtonClass}>Contact Support</a></li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </aside>
        )}

        <main className={`${currentRoute === 'home' ? 'flex-1 min-w-0' : 'w-full'} ${currentRoute === 'plate-detail' ? '' : 'p-4'} overflow-y-auto`}>
          {renderPage()}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ResponsiveLayout;
